<template>
  <div
    class="px-5 py-6 h-full flex flex-col overflow-y-auto"
    style="padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 0))"
  >
    <div class="flex items-start mb-6">
      <Logo class="h-3" />
    </div>

    <h1 class="text-[24px] font-bold mb-6 text-text-primary leading-tight">
      {{ step === 2 ? 'Verify your Number' : step === 1 ? 'Sign Up' : 'Complete Registration' }}
    </h1>

    <form @submit.prevent="handleSubmit" class="flex flex-col gap-6 flex-1">
      <div class="flex-1">
        <div v-if="step === 1" class="flex flex-col gap-2">
          <MobileInput
            v-model="mobileNo"
            label="Enter Your Mobile Number"
            placeholder="Enter mobile number"
            :disabled="loading"
            :error="mobileError"
            hint="We'll send a verification code to your number."
            id="mobile-no"
            @blur="validateMobile"
            @update:model-value="handleMobileInputUpdate"
          />
        </div>

        <div v-if="step === 2" class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <p class="text-[14px] text-text-secondary text-left">
              Enter the code sent to {{ formattedMobileDisplay }} to continue.
            </p>
            <OtpInput
              v-model="otp"
              :disabled="loading"
              :error="otpError"
              id="otp"
              @blur="validateOtp"
              @update:model-value="handleOtpInputUpdate"
            />
          </div>

          <div class="text-left text-[14px] text-text-secondary">
            Resend code in {{ formattedResendTime }}
          </div>

          <Button
            type="button"
            variant="text"
            size="small"
            :disabled="loading || !canResend"
            class="text-start"
            @click="handleResendOtp"
          >
            {{ canResend ? 'Resend OTP' : '' }}
          </Button>
        </div>

        <div v-if="step === 3" class="flex flex-col gap-4">
          <div v-if="errors.general" class="text-danger text-[14px]">{{ errors.general }}</div>

          <Input
            v-model="firstName"
            label="First Name"
            placeholder="Enter first name"
            :disabled="loading"
            :error="errors.first_name"
            id="first-name"
            @blur="validateFirstName"
          />

          <Input
            v-model="lastName"
            label="Last Name"
            placeholder="Enter last name"
            :disabled="loading"
            :error="errors.last_name"
            id="last-name"
            @blur="validateLastName"
          />

          <Input
            v-model="email"
            label="Email (Optional)"
            placeholder="Enter email"
            type="email"
            :disabled="loading"
            :error="errors.email"
            id="email"
          />
        </div>
      </div>

      <div class="mt-auto">
        <Button
          type="submit"
          variant="primary"
          size="default"
          :disabled="loading || !isFormValid"
          :loading="loading"
          full-width
        >
          {{ step === 1 ? 'Next' : step === 2 ? 'Confirm' : 'Complete Registration' }}
        </Button>

        <div v-if="step === 1" class="text-start text-[14px] text-text-secondary mt-2">
          Already have an account?
          <button
            type="button"
            class="text-primary no-underline hover:underline bg-transparent border-none cursor-pointer ml-1 text-[14px]"
            @click="handleSignInClick"
          >
            Sign In
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import Logo from '@/components/atoms/Logo.vue'
import Button from '@/components/common/Button.vue'
import Input from '@/components/common/Input.vue'
import MobileInput from '@/components/common/MobileInput.vue'
import OtpInput from '@/components/common/OtpInput.vue'
import type { OtpRequest } from '@/interfaces'
import { authApi } from '@/api/auth'
import { useMobileValidation } from '@/composables/features/useMobileValidation'
import { useOtpValidation } from '@/composables/features/useOtpValidation'
import { useResendTimer } from '@/composables/features/useResendTimer'
import { parseApiError, parseFieldError } from '@/utils/errorHandler'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const {
  mobileNo,
  mobileError,
  formattedMobileNo,
  formattedMobileDisplay,
  isValid: isMobileValid,
  handleMobileInputUpdate,
  validateMobile,
  setError: setMobileError,
} = useMobileValidation()

const {
  otp,
  otpError,
  isValid: isOtpValid,
  handleOtpInputUpdate: handleOtpUpdate,
  validateOtp,
  setError: setOtpError,
} = useOtpValidation()

const { formattedResendTime, canResend, startCooldown: startResendCooldown } = useResendTimer(30)

const step = ref<number>(1)
const firstName = ref<string>('')
const lastName = ref<string>('')
const email = ref<string>('')
const loading = ref<boolean>(false)
const errors = ref<Record<string, string>>({})
const timeRemaining = ref<number>(120)
let countdownTimer: ReturnType<typeof setInterval> | null = null

const isFormValid = computed(() => {
  if (step.value === 1) {
    return isMobileValid.value
  } else if (step.value === 2) {
    return isOtpValid.value
  } else {
    return firstName.value.trim().length > 0 && lastName.value.trim().length > 0
  }
})

const validateFirstName = (): boolean => {
  if (!firstName.value.trim()) {
    errors.value.first_name = 'First name is required'
    return false
  }
  delete errors.value.first_name
  return true
}

const validateLastName = (): boolean => {
  if (!lastName.value.trim()) {
    errors.value.last_name = 'Last name is required'
    return false
  }
  delete errors.value.last_name
  return true
}

const handleOtpInputUpdate = (value: string | number): void => {
  handleOtpUpdate(value)
  if (otp.value.length === 4 && step.value === 2) {
    handleVerifyOtp()
  }
}

const handleSubmit = async (): Promise<void> => {
  if (step.value === 1) {
    await handleSendOtp()
  } else if (step.value === 2) {
    await handleVerifyOtp()
  } else if (step.value === 3) {
    await handleCompleteRegistration()
  }
}

const handleSendOtp = async (): Promise<void> => {
  if (!validateMobile()) return

  loading.value = true

  try {
    const data: OtpRequest = {
      mobile_no: formattedMobileNo.value,
      account_exists: 'No',
    }

    await authStore.sendOTP(data)
    step.value = 2
    startResendCooldown()
    startCountdown()
  } catch (error: unknown) {
    const fieldError = parseFieldError(error, 'mobile_no')
    setMobileError(fieldError || parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleVerifyOtp = async (): Promise<void> => {
  if (!validateOtp()) return

  loading.value = true

  try {
    const response = await authApi.checkOtpValidity({
      mobile_no: formattedMobileNo.value,
      otp: otp.value,
    })

    if (response.error) {
      const errorMsg =
        typeof response.error === 'string'
          ? response.error
          : response.error.otp?.[0] || 'OTP verification failed'
      throw new Error(errorMsg)
    }

    step.value = 3
    stopCountdown()
  } catch (error: unknown) {
    const fieldError = parseFieldError(error, 'otp')
    setOtpError(fieldError || parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleCompleteRegistration = async (): Promise<void> => {
  if (!validateFirstName() || !validateLastName()) return

  loading.value = true
  errors.value = {}

  try {
    const registrationData: {
      mobile_no: string
      first_name: string
      last_name: string
      email?: string
    } = {
      mobile_no: formattedMobileNo.value,
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
    }

    if (email.value.trim()) {
      registrationData.email = email.value.trim()
    }

    await authStore.checkCustomerInfoValidity(registrationData)

    const newQuery = { ...route.query }
    delete newQuery.drawer
    router.replace({
      path: route.path,
      query: newQuery,
    })
  } catch (error: unknown) {
    const parsed = parseApiError(error)
    const firstNameError = parseFieldError(error, 'first_name')
    const lastNameError = parseFieldError(error, 'last_name')
    const mobileError = parseFieldError(error, 'mobile_no')

    if (firstNameError) {
      errors.value.first_name = firstNameError
    }
    if (lastNameError) {
      errors.value.last_name = lastNameError
    }
    if (mobileError) {
      setMobileError(mobileError)
    }

    if (!errors.value.first_name && !errors.value.last_name && !mobileError) {
      errors.value.general = parsed.message
    }
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async (): Promise<void> => {
  if (!canResend.value) return

  loading.value = true

  try {
    await authStore.resendOTP({
      mobile_no: formattedMobileNo.value,
      account_exists: 'No',
    })
    startResendCooldown()
    startCountdown()
  } catch (error: unknown) {
    setOtpError(parseApiError(error).message)
  } finally {
    loading.value = false
  }
}

const handleSignInClick = (): void => {
  const newQuery = { ...route.query }
  delete newQuery.drawer
  router.push({
    path: route.path,
    query: { ...newQuery, drawer: 'login' },
  })
}

const startCountdown = (): void => {
  timeRemaining.value = 120

  if (countdownTimer) {
    clearInterval(countdownTimer)
  }

  countdownTimer = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--
    } else {
      stopCountdown()
    }
  }, 1000)
}

const stopCountdown = (): void => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>
